import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Form, message } from 'antd';
import { PropmtDependentPackagesStore } from '../../stores';
import './ReprocessDependentPackagesButton.less';

interface Props {
    store: PropmtDependentPackagesStore;
    updatePromptTemplate: () => Promise<boolean>;
}

const ReprocessDependentPackagesButton: React.FC<Props> = ({ store, updatePromptTemplate }: Props) => {
    const [running, setRunning] = React.useState(false);
    const mounted = React.useRef(true);
    React.useEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );
    const label = React.useMemo(() => {
        if (store.isLoading) {
            return 'Loading Dependent Documents...';
        }

        if (!store.dependentPackageCount) {
            return 'No Dependent Documents Found';
        }

        return `Reprocess ${store.dependentPackageCount} Dependent ${
            store.dependentPackageCount === 1 ? 'Document' : 'Documents'
        }`;
    }, [store.isLoading, store.dependentPackageCount]);

    const handleReprocess = async () => {
        // We leave the page after saving the prompt, store will be disposed, so we need to keep package IDs
        const documents = store.dependentPackages.map(({ id, fileName }) => ({ id, fileName }));
        const projectId = store.project?.id;
        const templateId = store.selectedTemplate?.id;
        if (running || !projectId || !templateId) {
            return;
        }
        setRunning(true);
        try {
            if (await updatePromptTemplate()) {
                await store.reprocessDependentPackages(documents, projectId, templateId);
            }
        } catch {
            message.error('Unable to save the prompt and start reprocessing.');
        } finally {
            if (mounted.current) {
                setRunning(false);
            }
        }
    };

    React.useEffect(() => {
        if (store.selectedTemplate) {
            store.getDependentPackages();
        }

        return () => store.dispose();
    }, [store, store.selectedTemplate]);

    return (
        <Form.Item shouldUpdate noStyle>
            {({ getFieldsError }) => {
                const formHasErrors = getFieldsError().some(({ errors }) => errors.length > 0);
                const disabled = formHasErrors || store.reprocessDisabled || running;
                return (
                    <Button
                        className={`reprocess-dependent-packages-button ${disabled ? 'disabled' : 'enabled'}`}
                        key="reprocess-dependent-packages"
                        data-id="button-reprocess-dependent-packages"
                        size="large"
                        onClick={handleReprocess}
                        loading={running}
                        disabled={disabled}
                    >
                        {label}
                    </Button>
                );
            }}
        </Form.Item>
    );
};

export default observer(ReprocessDependentPackagesButton);
